const STORAGE_KEY = "pageTabs";
const EVENT_NAME = "simms:page-tabs-changed";
const MAX_HEADER_TABS = 5;
const MAX_CHILD_TABS = 8;

function safeParse(raw) {
    if (!raw) return null;
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
}

function normalizePathVariable(pathVariable) {
    const params = pathVariable ?? {};
    const next = {};
    Object.keys(params).forEach((k) => {
        const v = params[k];
        next[k] = v == null ? "" : String(v);
    });
    return next;
}

function buildTabKey(url, pathVariable) {
    const params = normalizePathVariable(pathVariable);
    const keys = Object.keys(params);
    if (keys.length === 0) return url ?? "";
    const sortedEntries = keys.sort().map((k) => [k, params[k]]);
    return `${url}::${JSON.stringify(sortedEntries)}`;
}

/** 탭에 저장된 실제 경로 (앞에 / 보장) */
export function getTabHref(tab) {
    if (!tab?.url) return "/";
    const url = tab.url;
    if (!/:([A-Za-z0-9_]+)/.test(url)) {
        return url.startsWith("/") ? url : `/${url}`;
    }
    const params = normalizePathVariable(tab.pathVariable);
    if (Object.keys(params).length === 0) {
        return url.startsWith("/") ? url : `/${url}`;
    }
    return url.replace(/:([A-Za-z0-9_]+)\??/g, (_, key) => {
        const value = params[key];
        return value != null ? encodeURIComponent(String(value)) : "";
    });
}

function getRoot() {
    const parsed = safeParse(localStorage.getItem(STORAGE_KEY));
    if (!parsed) return { headerTabs: [] };
    if (Array.isArray(parsed)) {
        // v1 호환: 평탄 배열 → 헤더 탭으로 마이그레이션
        return {
            headerTabs: parsed.map((t) => ({
                title: t.title,
                url: t.url,
                pathVariable: normalizePathVariable(t.pathVariable),
                childTabs: [],
            })),
        };
    }
    return {
        headerTabs: Array.isArray(parsed.headerTabs)
            ? parsed.headerTabs.map((h) => ({
                ...h,
                pathVariable: normalizePathVariable(h.pathVariable),
                childTabs: Array.isArray(h.childTabs) ? h.childTabs : [],
            }))
            : [],
    };
}

function notify(root) {
    window.dispatchEvent(new CustomEvent(EVENT_NAME, { detail: root }));
}

function writeRoot(root) {
    const normalized = {
        headerTabs: (root.headerTabs ?? []).map((h, idx) => ({
            title: h.title,
            url: h.url,
            pathVariable: normalizePathVariable(h.pathVariable),
            order: idx,
            pinned: !!h.pinned,
            icon: h.icon ?? null,
            childTabs: (h.childTabs ?? []).map((c, cidx) => ({
                title: c.title,
                url: c.url,
                pathVariable: normalizePathVariable(c.pathVariable),
                order: cidx,
                pinned: !!c.pinned,
            })),
        })),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(normalized));
    notify(normalized);
    return normalized;
}

/** max 개수를 초과하면 앞쪽(오래된 것)부터 pinned 가 아닌 탭만 제거 */
function trimUnpinned(arr, max) {
    if (arr.length <= max) return arr;
    const result = arr.slice();
    while (result.length > max) {
        const idx = result.findIndex((t) => !t.pinned);
        if (idx < 0) break;
        result.splice(idx, 1);
    }
    return result;
}

function findHeaderIdx(headers, url, pathVariable) {
    const key = buildTabKey(url, pathVariable);
    return headers.findIndex((h) => buildTabKey(h.url, h.pathVariable) === key);
}

function findChildIdx(children, url, pathVariable) {
    const key = buildTabKey(url, pathVariable);
    return children.findIndex((c) => buildTabKey(c.url, c.pathVariable) === key);
}

/** @deprecated getTabHref(tab) 또는 tab.url 직접 사용 */
export function resolveTabHref(tab) {
    return getTabHref(tab);
}

function pathMatches(tab, pathname) {
    if (!pathname || !tab?.url) return false;
    const href = getTabHref(tab);
    if (!href) return false;
    return pathname === href || pathname.startsWith(`${href}/`);
}

// ============ Header Tabs ============

export function getHeaderTabs() {
    return getRoot().headerTabs;
}

export function addHeaderTab({ title, url, pathVariable, icon }) {
    if (!url || !title) return getHeaderTabs();
    const root = getRoot();
    const idx = findHeaderIdx(root.headerTabs, url, pathVariable);

    if (idx >= 0) {
        const existing = root.headerTabs[idx];
        const nextIcon = icon ?? existing.icon ?? null;
        if (existing.title === title && existing.icon === nextIcon) return root.headerTabs;
        root.headerTabs[idx] = { ...existing, title, icon: nextIcon };
        return writeRoot(root).headerTabs;
    }

    const appended = [
        ...root.headerTabs,
        {
            title,
            url,
            pathVariable: normalizePathVariable(pathVariable),
            pinned: false,
            icon: icon ?? null,
            childTabs: [],
        },
    ];
    const trimmed = trimUnpinned(appended, MAX_HEADER_TABS);
    return writeRoot({ headerTabs: trimmed }).headerTabs;
}

export function removeHeaderTab({ url, pathVariable }) {
    const root = getRoot();
    const key = buildTabKey(url, pathVariable);
    const next = root.headerTabs.filter((h) => buildTabKey(h.url, h.pathVariable) !== key);
    return writeRoot({ headerTabs: next }).headerTabs;
}

export function reorderHeaderTabs(fromIndex, toIndex) {
    const root = getRoot();
    if (
        fromIndex < 0 || fromIndex >= root.headerTabs.length ||
        toIndex < 0 || toIndex >= root.headerTabs.length ||
        fromIndex === toIndex
    ) return root.headerTabs;
    const next = root.headerTabs.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    return writeRoot({ headerTabs: next }).headerTabs;
}

// ============ Child Tabs ============

function resolveParentHeaderIdx(headers, options) {
    const { headerUrl, headerPathVariable } = options ?? {};
    if (headerUrl != null && headerUrl !== "") {
        return findHeaderIdx(headers, headerUrl, headerPathVariable);
    }
    const pathname = typeof window !== "undefined" ? window.location.pathname : "";
    const exact = headers.findIndex((h) => pathMatches(h, pathname));
    if (exact >= 0) return exact;
    // 자식이 현재 경로와 매칭되는 헤더
    for (let i = 0; i < headers.length; i++) {
        const matched = (headers[i].childTabs ?? []).some((c) => pathMatches(c, pathname));
        if (matched) return i;
    }
    return headers.length - 1;
}

/** 목록(헤더 탭) + 상세(하위 탭)을 한 번에 등록 */
export function openHeaderTabWithChild({ header, child }) {
    if (!header?.url || !header?.title) return getHeaderTabs();

    addHeaderTab({
        title: header.title,
        url: header.url,
        pathVariable: header.pathVariable,
        icon: header.icon,
    });

    if (child?.url && child?.title) {
        addChildTab(
            { title: child.title, url: child.url, pathVariable: child.pathVariable },
            { headerUrl: header.url, headerPathVariable: header.pathVariable }
        );
    }

    return getHeaderTabs();
}

export function addChildTab(child, options) {
    if (!child?.url || !child?.title) return getHeaderTabs();
    let opts = options ?? {};
    if ((opts.headerUrl == null || opts.headerUrl === "") && isAssetInfoDetailUrl(child.url)) {
        opts = { ...opts, headerUrl: ASSET_INFO_LIST_TAB.url };
        const headers = getHeaderTabs();
        if (findHeaderIdx(headers, ASSET_INFO_LIST_TAB.url) < 0) {
            addHeaderTab({ title: ASSET_INFO_LIST_TAB.title, url: ASSET_INFO_LIST_TAB.url });
        }
    }
    const root = getRoot();
    const parentIdx = resolveParentHeaderIdx(root.headerTabs, opts);
    if (parentIdx < 0) return root.headerTabs;

    const parent = root.headerTabs[parentIdx];
    const children = parent.childTabs ?? [];
    const cidx = findChildIdx(children, child.url, child.pathVariable);

    if (cidx >= 0) {
        if (children[cidx].title === child.title) return root.headerTabs;
        const newChildren = children.slice();
        newChildren[cidx] = { ...newChildren[cidx], title: child.title };
        root.headerTabs[parentIdx] = { ...parent, childTabs: newChildren };
        return writeRoot(root).headerTabs;
    }

    const appended = [
        ...children,
        { title: child.title, url: child.url, pathVariable: normalizePathVariable(child.pathVariable), pinned: false },
    ];
    const trimmed = trimUnpinned(appended, MAX_CHILD_TABS);
    root.headerTabs[parentIdx] = { ...parent, childTabs: trimmed };
    return writeRoot(root).headerTabs;
}

// ============ 핀 토글 ============

export function toggleHeaderTabPin({ url, pathVariable }) {
    const root = getRoot();
    const idx = findHeaderIdx(root.headerTabs, url, pathVariable);
    if (idx < 0) return root.headerTabs;
    const cur = root.headerTabs[idx];
    root.headerTabs[idx] = { ...cur, pinned: !cur.pinned };
    return writeRoot(root).headerTabs;
}

export function toggleChildTabPin({ url, pathVariable, headerUrl, headerPathVariable }) {
    const root = getRoot();
    let parentIdx = -1;
    if (headerUrl) {
        parentIdx = findHeaderIdx(root.headerTabs, headerUrl, headerPathVariable);
    }
    if (parentIdx < 0) {
        for (let i = 0; i < root.headerTabs.length; i++) {
            const cidx = findChildIdx(root.headerTabs[i].childTabs ?? [], url, pathVariable);
            if (cidx >= 0) { parentIdx = i; break; }
        }
    }
    if (parentIdx < 0) return root.headerTabs;
    const parent = root.headerTabs[parentIdx];
    const cidx = findChildIdx(parent.childTabs ?? [], url, pathVariable);
    if (cidx < 0) return root.headerTabs;
    const newChildren = parent.childTabs.slice();
    newChildren[cidx] = { ...newChildren[cidx], pinned: !newChildren[cidx].pinned };
    root.headerTabs[parentIdx] = { ...parent, childTabs: newChildren };
    return writeRoot(root).headerTabs;
}

export function removeChildTab({ url, pathVariable, headerUrl, headerPathVariable }) {
    const root = getRoot();
    let parentIdx = -1;

    if (headerUrl) {
        parentIdx = findHeaderIdx(root.headerTabs, headerUrl, headerPathVariable);
    }
    if (parentIdx < 0) {
        for (let i = 0; i < root.headerTabs.length; i++) {
            const cidx = findChildIdx(root.headerTabs[i].childTabs ?? [], url, pathVariable);
            if (cidx >= 0) { parentIdx = i; break; }
        }
    }
    if (parentIdx < 0) return root.headerTabs;

    const parent = root.headerTabs[parentIdx];
    const key = buildTabKey(url, pathVariable);
    const newChildren = (parent.childTabs ?? []).filter(
        (c) => buildTabKey(c.url, c.pathVariable) !== key
    );
    root.headerTabs[parentIdx] = { ...parent, childTabs: newChildren };
    return writeRoot(root).headerTabs;
}

export function reorderChildTabs(headerKey, fromIndex, toIndex) {
    const root = getRoot();
    const parentIdx = findHeaderIdx(root.headerTabs, headerKey.url, headerKey.pathVariable);
    if (parentIdx < 0) return root.headerTabs;

    const parent = root.headerTabs[parentIdx];
    const children = parent.childTabs ?? [];
    if (
        fromIndex < 0 || fromIndex >= children.length ||
        toIndex < 0 || toIndex >= children.length ||
        fromIndex === toIndex
    ) return root.headerTabs;

    const next = children.slice();
    const [moved] = next.splice(fromIndex, 1);
    next.splice(toIndex, 0, moved);
    root.headerTabs[parentIdx] = { ...parent, childTabs: next };
    return writeRoot(root).headerTabs;
}

// ============ 통합 ============

/** 헤더 / 자식 어디든 url+pathVariable 로 찾아 title 갱신 */
export function updatePageTabTitle({ url, pathVariable, title }) {
    if (!url || !title) return getHeaderTabs();
    const root = getRoot();
    const key = buildTabKey(url, pathVariable);
    let changed = false;

    for (let i = 0; i < root.headerTabs.length; i++) {
        const h = root.headerTabs[i];
        if (buildTabKey(h.url, h.pathVariable) === key) {
            if (h.title !== title) {
                root.headerTabs[i] = { ...h, title };
                changed = true;
            }
            break;
        }
        const children = h.childTabs ?? [];
        let childChanged = false;
        const newChildren = children.slice();
        for (let j = 0; j < children.length; j++) {
            if (buildTabKey(children[j].url, children[j].pathVariable) === key) {
                if (children[j].title !== title) {
                    newChildren[j] = { ...children[j], title };
                    childChanged = true;
                }
                break;
            }
        }
        if (childChanged) {
            root.headerTabs[i] = { ...h, childTabs: newChildren };
            changed = true;
            break;
        }
    }
    return changed ? writeRoot(root).headerTabs : root.headerTabs;
}

/** 자산정보관리 목록의 자산 상세 (/asset/:숫자) — eos·list·purchase 등 다른 /asset/* 경로 제외 */
export function isAssetInfoDetailUrl(url) {
    if (!url || typeof url !== "string") return false;
    const path = url.split("?")[0].split("#")[0];
    return /^\/asset\/\d+\/?$/.test(path);
}

export const ASSET_INFO_LIST_TAB = {
    url: "/asset/list",
    title: "자산정보관리",
};

/** 목록 행에서 하위 탭 제목 후보 추출 */
export function pickDetailTabTitle(row, fallback = "상세") {
    if (!row || typeof row !== "object") return fallback;
    const candidates = [
        row.title,
        row.name,
        row.subject,
        row.assetName,
        row.asset?.assetName,
        row.number,
        row.purchaseNumber,
        row.serialNumber,
    ];
    for (const value of candidates) {
        const text = value == null ? "" : String(value).trim();
        if (text) return text;
    }
    return fallback;
}

/** 하위 탭 등록 후 상세 페이지로 이동 */
export function navigateWithChildTab(
    navigate,
    { title, url, headerUrl, headerPathVariable, headerTitle, state }
) {
    if (!url || typeof navigate !== "function") return;
    const tabTitle = title?.trim() || "상세";

    let parentUrl = headerUrl;
    let parentTitle = headerTitle;
    if ((parentUrl == null || parentUrl === "") && isAssetInfoDetailUrl(url)) {
        parentUrl = ASSET_INFO_LIST_TAB.url;
        parentTitle = parentTitle ?? ASSET_INFO_LIST_TAB.title;
    }

    if (parentUrl != null && parentUrl !== "") {
        const headers = getHeaderTabs();
        if (findHeaderIdx(headers, parentUrl, headerPathVariable) < 0 && parentTitle) {
            addHeaderTab({
                title: parentTitle,
                url: parentUrl,
                pathVariable: headerPathVariable,
            });
        }
        addChildTab({ title: tabTitle, url }, { headerUrl: parentUrl, headerPathVariable });
    } else {
        addChildTab({ title: tabTitle, url });
    }
    navigate(url, state ? { state } : undefined);
}

// ============ 호환 ============

/** 호환: 사이드바 클릭 시 사용 — 헤더 탭과 동일 */
export function addPageTab(opts) {
    return addHeaderTab(opts);
}

/** 호환: url + pathVariable 로 헤더든 자식이든 제거 */
export function removePageTab({ url, pathVariable }) {
    const root = getRoot();
    const hidx = findHeaderIdx(root.headerTabs, url, pathVariable);
    if (hidx >= 0) {
        root.headerTabs.splice(hidx, 1);
        return writeRoot(root).headerTabs;
    }
    for (let i = 0; i < root.headerTabs.length; i++) {
        const cidx = findChildIdx(root.headerTabs[i].childTabs ?? [], url, pathVariable);
        if (cidx >= 0) {
            const newChildren = root.headerTabs[i].childTabs.slice();
            newChildren.splice(cidx, 1);
            root.headerTabs[i] = { ...root.headerTabs[i], childTabs: newChildren };
            return writeRoot(root).headerTabs;
        }
    }
    return root.headerTabs;
}

export function clearPageTabs() {
    return writeRoot({ headerTabs: [] }).headerTabs;
}

/** 호환: 평탄한 탭 배열 반환 (활성 헤더의 자식까지 합쳐서) — 외부에서 사용 시 주의 */
export function getPageTabs() {
    return getHeaderTabs();
}

export const PAGE_TABS_EVENT = EVENT_NAME;
export const PAGE_TABS_LIMITS = { MAX_HEADER_TABS, MAX_CHILD_TABS };
